import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './ContactPage.css';

const IconSupport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const IconTips = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 16v-4M12 8h.01"/>
  </svg>
);

const IconBug = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3l-1.5 1.5M16 3l1.5 1.5M9 9H5M19 9h-4M12 20v-8M8 12a4 4 0 1 0 8 0v-2a4 4 0 1 0-8 0v2z"/>
    <path d="M5 16H3M21 16h-2M5 12H3M21 12h-2"/>
  </svg>
);

const IconImprovement = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9"/>
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
  </svg>
);

const IconCheck = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const CATEGORIES = [
  { key: 'support',     Icon: IconSupport },
  { key: 'tips',        Icon: IconTips },
  { key: 'bug',         Icon: IconBug },
  { key: 'improvement', Icon: IconImprovement },
];

async function submitContact({ name, email, message, category }) {
  const res = await fetch('/api/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, subject: category, message }),
  });
  if (!res.ok) throw new Error('Send failed');
  return true;
}

export default function ContactPage() {
  const { t } = useLanguage();
  const [category, setCategory] = useState('support');
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [message, setMessage]   = useState('');
  const [status, setStatus]     = useState('idle');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setStatus('sending');
    try {
      const ok = await submitContact({ name: name.trim(), email: email.trim(), message: message.trim(), category });
      setStatus(ok ? 'done' : 'error');
    } catch {
      setStatus('error');
    }
  }

  return (
    <div className="cp-page">
      <div className="cp-container">

        <div className="cp-hero">
          <div className="cp-tag">{t('contact.tag')}</div>
          <h1 className="cp-title">{t('contact.title')}</h1>
          <p className="cp-sub">{t('contact.sub')}</p>
        </div>

        {status === 'done' ? (
          <div className="cp-success">
            <div className="cp-success-icon"><IconCheck /></div>
            <h2>{t('contact.successTitle')}</h2>
            <p>{t('contact.successSub')}</p>
            <button
              className="cp-reset-btn"
              onClick={() => { setStatus('idle'); setName(''); setEmail(''); setMessage(''); setCategory('support'); }}
            >
              {t('contact.sendAnother')}
            </button>
          </div>
        ) : (
          <form className="cp-form" onSubmit={handleSubmit}>

            <div className="cp-section-label">{t('contact.categoryLabel')}</div>
            <div className="cp-categories">
              {CATEGORIES.map(({ key, Icon }) => (
                <button
                  type="button"
                  key={key}
                  className={`cp-cat-btn${category === key ? ' cp-cat-btn--active' : ''}`}
                  onClick={() => setCategory(key)}
                >
                  <span className="cp-cat-icon"><Icon /></span>
                  {t('contact.category.' + key)}
                </button>
              ))}
            </div>

            <div className="cp-fields">
              <div className="cp-field">
                <label className="cp-label">{t('contact.nameLabel')}</label>
                <input
                  className="cp-input"
                  type="text"
                  placeholder={t('contact.namePlaceholder')}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                />
              </div>
              <div className="cp-field">
                <label className="cp-label">{t('contact.emailLabel')}</label>
                <input
                  className="cp-input"
                  type="email"
                  placeholder={t('contact.emailPlaceholder')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="cp-field cp-field--full">
              <label className="cp-label">{t('contact.messageLabel')}</label>
              <textarea
                className="cp-textarea"
                placeholder={t('contact.messagePlaceholder')}
                rows={6}
                value={message}
                onChange={e => setMessage(e.target.value)}
                required
              />
            </div>

            {status === 'error' && (
              <p className="cp-error">{t('contact.errorMsg')}</p>
            )}

            <button
              type="submit"
              className="cp-submit-btn"
              disabled={status === 'sending'}
            >
              {status === 'sending' ? t('contact.sending') : t('contact.submit')}
            </button>

          </form>
        )}

      </div>
    </div>
  );
}
